"use client";

import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const features = [{
  number: "01",
  title: "Engenharia de Precisão.",
  body: "Sistemas de pulverização desenvolvidos com tecnologia de ponta para garantir eficiência máxima, redução de desperdício e controlo total em processos industriais complexos.",
  specLabel: "Eficiência",
  specValue: "99.9%"
}, {
  number: "02",
  title: "Bocais de Alta Performance.",
  body: "Componentes fabricados com materiais de alta resistência, projetados para suportar condições extremas e manter a precisão do padrão de pulverização ao longo do tempo.",
  specLabel: "Durabilidade",
  specValue: "Alta"
}, {
  number: "03",
  title: "Sistemas Automatizados.",
  body: "Integração de sistemas inteligentes que monitorizam o fluxo e a pressão em tempo real, assegurando resultados consistentes e otimização de recursos operacionais.",
  specLabel: "Controlo",
  specValue: "Total"
}, {
  number: "04",
  title: "Soluções Personalizadas.",
  body: "Projetos de engenharia à medida para cada setor, adaptando a tecnologia da Spraying Systems Co. às necessidades específicas da sua linha de produção industrial.",
  specLabel: "Adaptação",
  specValue: "100%"
}, {
  number: "05",
  title: "Suporte Técnico Especializado.",
  body: "Desde 1993, oferecemos consultoria técnica dedicada, garantindo a manutenção e a performance contínua dos seus sistemas de pulverização em todo o território nacional.",
  specLabel: "Experiência",
  specValue: "30+ anos"
}];
export default function Expertise() {
  const container = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const root = container.current;
    const leftEl = leftRef.current;
    const rightEl = rightRef.current;
    if (!root || !leftEl || !rightEl) return;
    const mm = gsap.matchMedia();
    mm.add("(min-width: 1024px) and (prefers-reduced-motion: no-preference)", () => {
      ScrollTrigger.create({
        trigger: leftEl,
        start: "top 96px",
        endTrigger: rightEl,
        end: "bottom bottom-=120",
        pin: leftEl,
        pinSpacing: false,
        anticipatePin: 1
      });
      gsap.set(".void-card", {
        y: 60
      });
      gsap.utils.toArray<HTMLElement>(".void-card").forEach((card, i) => {
        gsap.to(card, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          delay: i * 0.12,
          scrollTrigger: {
            trigger: card,
            start: "top 78%",
            once: true
          }
        });
      });
      gsap.set(".void-left-child", {
        y: 20
      });
      gsap.to(".void-left-child", {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        stagger: 0.09,
        scrollTrigger: {
          trigger: leftEl,
          start: "top 82%",
          once: true
        }
      });
    });
    mm.add("(max-width: 1023px) and (prefers-reduced-motion: no-preference)", () => {
      gsap.set(".void-card", {
        y: 40
      });
      gsap.utils.toArray<HTMLElement>(".void-card").forEach((card, i) => {
        gsap.to(card, {
          opacity: 1,
          y: 0,
          duration: 0.7,
          ease: "power3.out",
          delay: i * 0.1,
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            once: true
          }
        });
      });
      gsap.set(".void-left-child", {
        y: 20
      });
      gsap.to(".void-left-child", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        stagger: 0.09,
        scrollTrigger: {
          trigger: leftEl,
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".void-card", ".void-left-child"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section ref={container} id="expertise" className="bg-background text-foreground py-32 md:py-44 relative overflow-hidden">
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column (Sticky / Pinned) */}
          <div ref={leftRef} className="lg:col-span-5 relative">
            <div aria-hidden="true" className="pointer-events-none absolute -top-20 -left-20 w-[400px] h-[400px] rounded-lg opacity-40" style={{
            background: "radial-gradient(circle at center, hsl(215 90% 45% / 0.18) 0%, hsl(215 90% 45% / 0.06) 40%, transparent 70%)",
            filter: "blur(40px)"
          }} />

            <div className="relative">
              <div className="void-left-child inline-flex items-center gap-2 mb-8" style={{
              opacity: 0
            }}>
                <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-lg" style={{
                backgroundColor: "hsl(215 90% 45%)"
              }} />
                <span className="text-xs font-medium uppercase text-muted-foreground" style={{
                letterSpacing: "0.22em"
              }}>
                  EXPERTISE
                </span>
              </div>

              <h2 className="void-left-child font-default text-foreground" style={{
              opacity: 0,
              fontSize: "clamp(2rem, 4.4vw, 3.75rem)",
              fontWeight: 480,
              letterSpacing: "-0.03em",
              lineHeight: 1.08
            }}>
                Excelência técnica em engenharia de pulverização.
              </h2>

              <p className="void-left-child mt-8 text-muted-foreground max-w-[420px]" style={{
              opacity: 0,
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
              fontWeight: 350,
              lineHeight: 1.6,
              letterSpacing: "-0.005em"
            }}>
                Projetamos e fornecemos soluções avançadas de pulverização sob medida, combinando precisão microscópica, redução de desperdícios e elevada fiabilidade operacional para a indústria moderna.
              </p>

              <div className="void-left-child mt-10" style={{
              opacity: 0
            }}>
                <Button asChild variant="ghost" className="group h-auto px-0 py-2 text-foreground hover:bg-transparent hover:text-primary">
                  <Link to="/TechnicalSolutionsPage">
                    <span className="text-sm font-medium">Consultar soluções técnicas</span>
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5 group-hover:-translate-y-0.5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column (Cards List) */}
          <div ref={rightRef} className="lg:col-span-7 flex flex-col gap-4 lg:gap-5">
            {features.map((feature, i) => <div key={i} data-index={i} className="void-card group rounded-3xl p-6 lg:p-8 transition-all duration-300 hover:-translate-y-1" style={{
            opacity: 0,
            background: "hsla(210, 10%, 92%, 0.3)",
            backdropFilter: "blur(28px) saturate(140%)",
            WebkitBackdropFilter: "blur(28px) saturate(140%)",
            border: "1px solid hsla(210, 10%, 85%, 0.5)"
          }} onMouseEnter={e => {
            e.currentTarget.style.border = "1px solid hsl(215 90% 45%)";
            e.currentTarget.style.boxShadow = "0 24px 80px hsla(215 90% 45%, 0.1), inset 0 0 60px hsla(215 90% 45%, 0.05)";
          }} onMouseLeave={e => {
            e.currentTarget.style.border = "1px solid hsla(210, 10%, 85%, 0.5)";
            e.currentTarget.style.boxShadow = "none";
          }}>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-6 items-start">
                  <div className="flex items-start gap-4 sm:gap-6">
                    <span className="font-mono text-sm font-semibold tracking-wider pt-0.5 shrink-0" style={{
                  color: "hsl(215 90% 45%)"
                }}>
                      {feature.number}
                    </span>
                    <div>
                      <h3 className="font-default text-foreground" style={{
                    fontSize: "clamp(1.125rem, 1.3vw, 1.25rem)",
                    fontWeight: 500,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.3
                  }}>
                        {feature.title}
                      </h3>
                      <p className="mt-3 text-muted-foreground" style={{
                    fontSize: "clamp(0.9375rem, 1vw, 1rem)",
                    fontWeight: 350,
                    lineHeight: 1.6
                  }}>
                        {feature.body}
                      </p>
                    </div>
                  </div>

                  <div className="shrink-0 self-start sm:self-center px-4 py-3 rounded-2xl border border-border bg-card/70 text-left sm:text-right min-w-[130px]">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                      {feature.specLabel}
                    </div>
                    <div className="text-lg font-semibold tracking-tight mt-0.5" style={{
                  color: "hsl(215 90% 45%)"
                }}>
                      {feature.specValue}
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}